/**
 * Server functions for content loading.
 *
 * These use createServerFn — the handler runs ONLY on the server.
 * On the client, the build tooling replaces the handler with an RPC stub
 * that fetches from the server. This means:
 *   - content.ts, content-body.ts, markdown.ts are NEVER in the client bundle
 *   - The client makes a single fetch() per navigation, not 778 module loads
 *
 * Route loaders call these functions. The loader itself is isomorphic,
 * but the server function creates the server/client boundary.
 */
import { createServerFn } from "@tanstack/react-start";
import {
  getCookie,
  setResponseHeader,
} from "@tanstack/react-start/server";
import { notFound } from "@tanstack/react-router";
import { renderMdxToHtml } from "./mdx-render";

import {
  getBlogPost,
  getAllBlogPosts,
  getEngineeringPost,
  getAllEngineeringPosts,
  getGuide,
  getAllGuides,
  getGlossaryEntry,
  getAllGlossaryEntries,
  getNewsArticle,
  getAllNewsArticles,
  getPodcastEpisode,
  getAllPodcastEpisodes,
  getTalk,
  getAllTalks,
  getCustomer,
  getAllCustomers,
  getIntegration,
  getAllIntegrations,
  getIntegrationCategories,
  getIntegrationsByCategory,
  getAcademyLesson,
  getAllAcademyLessons,
  getLegalPage,
  getLandingPage,
  getSolution,
  getSolutionsTeam,
  getProduct,
  getSpPage,
  getRegisterPage,
  getResourcePage,
  getIndustryPage,
  filterForList,
  filterForSingle,
  filterForSitemap,
  paginate,
  POSTS_PER_PAGE,
  type BlogPost,
  type EngineeringPost,
  type Guide,
  type GlossaryEntry,
  type NewsArticle,
  type PodcastEpisode,
  type Talk,
  type Customer,
  type Integration,
  type AcademyLesson,
  type BlockPage,
  type ContentBase,
  getStandaloneFrontmatter,
  getStandaloneFrontmatterAll,
  getStandaloneBody,
  getStandaloneBodyAll,
  getPartnerPage,
} from "./content";

import {
  getBlogPostBody,
  getEngineeringPostBody,
  getGuideBody,
  getGlossaryBody,
  getNewsBody,
  getPodcastBody,
  getTalkBody,
  getCustomerBody,
  getIntegrationBody,
  getAcademyBody,
  getLegalBody,
} from "./content-body";

import { renderMarkdown } from "./markdown";
import { renderBlockMarkdownFields } from "./block-markdown";
import { getAuthors, resolveAuthors, getAuthor, allAuthorKeys, hasAuthorPage } from "./authors";
import type {
  AuthorView,
  AuthorCredit,
  AuthorPageView,
  AuthorSummaryView,
  BlogPostView,
  TocEntry,
  RelatedPost,
  EngineeringPostView,
  GuideView,
  GlossaryEntryView,
  NewsArticleView,
  PodcastEpisodeView,
  TalkView,
  CustomerView,
  IntegrationView,
  AcademyLessonView,
  LegalPageView,
  BlogListView,
  EngineeringListView,
  GlossaryListView,
  StandalonePageView,
  ContentBaseView,
} from "~/types/views";

/** Map ContentBase fields to serializable view, converting undefined → null */
function baseView(item: ContentBase, html: string): ContentBaseView {
  return {
    slug: item.slug,
    title: item.title,
    description: item.description,
    dek: item.dek ?? null,
    date: item.date,
    last_updated: item.last_updated ?? null,
    featured_image: item.featured_image,
    seo_title: item.seo_title ?? null,
    seo_description: item.seo_description ?? null,
    robots: item.robots ?? null,
    layout: item.layout ?? null,
    draft: item.draft ?? null,
    hidden: item.hidden ?? null,
    isMdx: item.isMdx ?? null,
    html,
  };
}

/** Map Author to serializable view */
function authorView(authors: ReturnType<typeof getAuthors>): AuthorView[] {
  return authors.map((a) => ({
    key: a.key,
    name: a.name,
    image: a.image,
    title: a.title,
    bio: a.bio,
    linkedin: a.linkedin,
    hasPage: hasAuthorPage(a.key),
  }));
}

/**
 * Everything an author is credited on, newest first.
 *
 * Reads the same three prose collections that carry an `author:` byline, and
 * runs each through `filterForList` so drafts and hidden items never surface
 * on a public author page.
 */
function creditsFor(key: string): AuthorCredit[] {
  const credit = (
    collection: AuthorCredit["collection"],
    items: Array<{
      slug: string;
      title: string;
      description: string;
      date: string;
      featured_image: string;
      author?: string[];
    }>,
  ): AuthorCredit[] =>
    filterForList(items as never)
      .filter((item: (typeof items)[number]) => (item.author ?? []).includes(key))
      .map((item: (typeof items)[number]) => ({
        slug: item.slug,
        title: item.title,
        description: item.description,
        date: item.date,
        featured_image: item.featured_image,
        collection,
      }));

  return [
    ...credit("blog", getAllBlogPosts()),
    ...credit("engineering", getAllEngineeringPosts()),
    ...credit("guides", getAllGuides()),
    ...credit("glossary", getAllGlossaryEntries()),
  ].sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
}

/** Author page: the person, plus everything they've published. */
export const loadAuthor = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data: { slug } }): Promise<AuthorPageView> => {
    const author = getAuthor(slug);
    // An author with no record, or the collective C1 byline, has no page.
    if (!author || !hasAuthorPage(slug)) throw notFound();
    const credits = creditsFor(slug);
    // Nobody should get an empty page — it is thin content and a dead end for
    // anyone who follows a byline to it.
    if (credits.length === 0) throw notFound();
    return {
      key: author.key,
      name: author.name,
      title: author.title,
      image: author.image,
      bio: author.bio,
      linkedin: author.linkedin,
      credits,
    };
  });

/** Author index: every author who has published something, most prolific first. */
export const loadAuthorIndex = createServerFn({ method: "GET" }).handler(
  async (): Promise<AuthorSummaryView[]> =>
    allAuthorKeys()
      .map((key) => {
        const a = getAuthor(key);
        if (!a) return null;
        const count = creditsFor(key).length;
        return count === 0
          ? null
          : { key, name: a.name, title: a.title, image: a.image, bio: a.bio, count };
      })
      .filter((a): a is AuthorSummaryView => a !== null)
      .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name)),
);

// ================================================================
// Single-page loaders (frontmatter + rendered body)
// ================================================================

function extractToc(html: string): TocEntry[] {
  const entries: TocEntry[] = [];
  const re = /<h2[^>]*id="([^"]*)"[^>]*>(.*?)<\/h2>/gi;
  let m: RegExpExecArray | null;
  while ((m = re.exec(html)) !== null) {
    // Strip HTML tags, then remove trailing "#" left by rehype-autolink-headings
    const text = m[2].replace(/<[^>]+>/g, "").replace(/\s*#\s*$/, "").trim();
    if (text) entries.push({ id: m[1], text });
  }
  return entries;
}

function plainTextSummary(input: string, maxLength = 180): string {
  const text = input
    .replace(/<script[\s\S]*?<\/script>/gi, " ")
    .replace(/<style[\s\S]*?<\/style>/gi, " ")
    .replace(/!\[[^\]]*]\([^)]*\)/g, " ")
    .replace(/\[([^\]]+)]\([^)]*\)/g, "$1")
    .replace(/<[^>]+>/g, " ")
    .replace(/[#*_`~>|-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (text.length <= maxLength) return text;

  const truncated = text.slice(0, maxLength + 1);
  const lastSpace = truncated.lastIndexOf(" ");
  return `${truncated.slice(0, lastSpace > 80 ? lastSpace : maxLength).trim()}...`;
}

function integrationDescription(
  integration: Integration,
  body: string,
  html: string,
): string {
  const summary = integration.description || plainTextSummary(body || html);
  if (summary) return summary;

  const appName = integration.app || integration.title || "this app";
  return `Manage and secure access for ${appName} with C1.ai. Automate provisioning, access requests, and access reviews across your identity environment.`;
}

function getRelatedBlogPosts(
  currentSlug: string,
  count: number,
): RelatedPost[] {
  const all = filterForList(getAllBlogPosts());
  return all
    .filter((p) => p.slug !== currentSlug)
    .slice(0, count)
    .map((p) => ({
      slug: p.slug,
      title: p.title,
      featured_image: p.featured_image,
      date: p.date,
    }));
}

export const loadBlogPost = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data: { slug } }): Promise<BlogPostView> => {
    const post = getBlogPost(slug);
    if (!post) throw notFound();
    const html = post.isMdx
      ? renderMdxToHtml("blog", slug)
      : await renderMarkdown(await getBlogPostBody(slug, post._filename));
    return {
      ...baseView(post, html),
      categories: post.categories,
      authors: authorView(getAuthors(post.author)),
      featured_video: post.featured_video ?? null,
      featured_image_position: post.featured_image_position ?? null,
      toc: extractToc(html),
      relatedPosts: getRelatedBlogPosts(slug, 3),
    };
  });

function estimateReadingTime(html: string): number {
  const text = html.replace(/<[^>]+>/g, " ");
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 250));
}

function getRelatedEngineeringPosts(
  currentSlug: string,
  count: number,
): RelatedPost[] {
  const all = filterForList(getAllEngineeringPosts());
  const currentIndex = all.findIndex((post) => post.slug === currentSlug);
  if (currentIndex < 0) return [];

  return all.slice(currentIndex + 1, currentIndex + 1 + count).map((post) => ({
    slug: post.slug,
    title: post.title,
    featured_image: post.featured_image,
    date: post.date,
  }));
}

export const loadEngineeringPost = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data: { slug } }): Promise<EngineeringPostView> => {
    const post = getEngineeringPost(slug);
    if (!post) throw notFound();
    const html = post.isMdx
      ? renderMdxToHtml("engineering", slug)
      : await renderMarkdown(await getEngineeringPostBody(slug, post._filename));
    return {
      ...baseView(post, html),
      categories: post.categories,
      authors: authorView(getAuthors(post.author)),
      readingTime: estimateReadingTime(html),
      toc: extractToc(html),
      relatedPosts: getRelatedEngineeringPosts(slug, 3),
    };
  });

export const loadGuide = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data: { slug } }): Promise<GuideView> => {
    const guide = getGuide(slug);
    if (!guide) throw notFound();

    // Block-based guide — return blocks instead of rendered HTML
    if (guide.layout === "blocks") {
      const rawBlocks = hydrateLatestCards((guide.blocks ?? []) as any[]);
      const blocks = await renderBlockMarkdownFields(rawBlocks);
      return {
        ...baseView(guide, ""),
        authors: authorView(resolveAuthors(guide.author)),
        download: guide.download ?? null,
        gated: guide.gated ?? null,
        image: guide.image ?? null,
        bg_swipe: guide.bg_swipe ?? null,
        toc: [],
        blocks: blocks as GuideView["blocks"],
        faq: guide.faq ?? null,
      };
    }

    // Standard markdown guide
    const html = guide.isMdx
      ? renderMdxToHtml("guides", slug)
      : await renderMarkdown(await getGuideBody(slug, guide._filename));
    return {
      ...baseView(guide, html),
      authors: authorView(resolveAuthors(guide.author)),
      download: guide.download ?? null,
      gated: guide.gated ?? null,
      image: guide.image ?? null,
      bg_swipe: guide.bg_swipe ?? null,
      hide_heading_anchors: guide.hide_heading_anchors ?? null,
      toc: extractToc(html),
      blocks: null,
      faq: guide.faq ?? null,
    };
  });

function getRelatedGlossaryEntries(
  currentSlug: string,
  count: number,
): RelatedPost[] {
  const all = filterForList(getAllGlossaryEntries());
  return all
    .filter((p) => p.slug !== currentSlug)
    .slice(0, count)
    .map((p) => ({
      slug: p.slug,
      title: p.title,
      featured_image: p.featured_image,
      date: p.date,
    }));
}

export const loadGlossaryEntry = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data: { slug } }): Promise<GlossaryEntryView> => {
    const entry = getGlossaryEntry(slug);
    if (!entry) throw notFound();
    const html = entry.isMdx
      ? renderMdxToHtml("glossary", slug)
      : await renderMarkdown(await getGlossaryBody(slug, entry._filename));
    return {
      ...baseView(entry, html),
      term: entry.term ?? null,
      termName: entry.term_name ?? null,
      authors: authorView(getAuthors(entry.author ?? [])),
      alternateNames: entry.alternate_names ?? [],
      faq: entry.faq ?? [],
      toc: extractToc(html),
      relatedPosts: getRelatedGlossaryEntries(slug, 3),
    };
  });

export const loadNewsArticle = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data: { slug } }): Promise<NewsArticleView> => {
    const article = getNewsArticle(slug);
    if (!article) throw notFound();
    const body = await getNewsBody(slug, article._filename);
    const html = await renderMarkdown(body);
    return {
      ...baseView(article, html),
      _subpath: article._subpath ?? null,
      authors: authorView(
        getAuthors(article.author ? [article.author] : []),
      ),
      featured_video: article.featured_video ?? null,
      featured_image_position: article.featured_image_position ?? null,
      toc: extractToc(html),
      newsCards: (article.news?.cards ?? []).map((c) => ({
        heading: c.heading,
        date: c.date ?? null,
        image: c.image ?? null,
        url: c.url,
      })),
    };
  });

export const loadPodcastEpisode = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data: { slug } }): Promise<PodcastEpisodeView> => {
    const ep = getPodcastEpisode(slug);
    if (!ep) throw notFound();
    const body = await getPodcastBody(slug, ep._filename);
    const html = await renderMarkdown(body);
    return {
      ...baseView(ep, html),
      author: ep.author ?? null,
      heading_color: ep.heading_color ?? null,
      thumbnail: ep.thumbnail ?? null,
    };
  });

export const loadTalk = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data: { slug } }): Promise<TalkView> => {
    const talk = getTalk(slug);
    if (!talk) throw notFound();
    const body = await getTalkBody(slug, talk._filename);
    const html = await renderMarkdown(body);
    const gated = Boolean(talk.hubspot_form_id);
    return {
      ...baseView(talk, html),
      speaker: talk.speaker ?? null,
      speaker_title: talk.speaker_title ?? null,
      // SECURITY: this loader backs the page's initial SSR render, which is
      // an ISR-cached response shared across every visitor of this URL (see
      // getGatedTalkVideo below / geo-fns.ts for the same landmine). It must
      // NEVER vary by cookie -- a single unlocked visitor's response could
      // get pinned into the shared edge cache for everyone. So gated talks
      // always get featured_video stripped here, full stop; the real URL is
      // fetched out-of-band, post-mount, via the no-store getGatedTalkVideo
      // server fn once the visitor has actually unlocked it client-side.
      featured_video: gated ? null : talk.featured_video ?? null,
      length: talk.length ?? null,
      hubspot_form_id: talk.hubspot_form_id ?? null,
      hubspot_portal_id:
        talk.hubspot_portal_id != null ? String(talk.hubspot_portal_id) : null,
      form_heading: talk.form_heading ?? null,
      form_copy: talk.form_copy ?? null,
      form_cta: talk.form_cta ?? null,
    };
  });

/**
 * Returns the playable video URL for a gated talk, but only once the visitor
 * has actually unlocked it (HubSpot form submit, or the marketing-email
 * bypass) -- signaled by a `talk-unlocked-<slug>` cookie the client sets
 * itself. Absent that cookie, returns null.
 *
 * CACHING: same landmine as getVisitorCountry in geo-fns.ts. ISR caches by
 * URL path, including POST requests to /_serverFn/*, so a per-visitor
 * response must opt out of the CDN cache or the first caller's result (one
 * way or the other) gets pinned for every subsequent visitor.
 *
 * NOTE: this is a client-readable/forgeable cookie, not a verified HubSpot
 * submission -- it stops the casual view-source/curl bypass (the video URL
 * never enters the SSR/hydration payload), not a determined attacker who
 * fabricates the cookie. True airtight gating needs server-side HubSpot
 * submission verification, which is out of scope here.
 */
export const getGatedTalkVideo = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(({ data: { slug } }): { featured_video: string | null } => {
    setResponseHeader("Cache-Control", "private, no-store");
    setResponseHeader("Vercel-CDN-Cache-Control", "no-store");

    const talk = getTalk(slug);
    if (!talk || !talk.hubspot_form_id) return { featured_video: null };

    const unlocked = getCookie(`talk-unlocked-${slug}`) === "1";
    if (!unlocked) return { featured_video: null };

    return { featured_video: talk.featured_video ?? null };
  });

export const loadCustomer = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data: { slug } }): Promise<CustomerView> => {
    const customer = getCustomer(slug);
    if (!customer) throw notFound();
    let html = "";
    if (customer.isMdx) {
      html = renderMdxToHtml("customers", slug);
    } else {
      const body = await getCustomerBody(slug, customer._filename);
      html = body.trim() ? await renderMarkdown(body) : "";
    }
    const q = customer.quote as Record<string, unknown> | undefined;
    const author = q?.author as Record<string, string> | undefined;
    return {
      ...baseView(customer, html),
      heading: customer.heading ?? null,
      cta: customer.cta ?? null,
      logo: customer.logo ?? null,
      logo_light: customer.logo_light ?? null,
      challenges: customer.challenges ?? null,
      results: customer.results ?? null,
      about: customer.about ?? null,
      industries: customer.industries ?? null,
      tags: customer.tags ?? null,
      company_size: customer.company_size ?? null,
      headquarters: customer.headquarters ?? null,
      video_url: customer.video_url ?? null,
      youtube: customer.youtube ?? null,
      logo_about: customer.logo_about ?? null,
      lead: customer.lead ?? null,
      button: customer.button ?? null,
      quote: q ? {
        copy: (q.copy as string) ?? null,
        author: author ? {
          name: author.name ?? null,
          title: author.title ?? null,
          image: (author.image as string) ?? null,
        } : null,
      } : null,
    };
  });

export const loadIntegration = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string; category?: string }) => d)
  .handler(async ({ data: { slug, category } }): Promise<IntegrationView> => {
    const integration = getIntegration(slug, category);
    if (!integration) throw notFound();
    const body = await getIntegrationBody(slug, integration._filename, integration._category);
    const html = body.trim() ? await renderMarkdown(body) : "";
    const description = integrationDescription(integration, body, html);
    return {
      ...baseView({ ...integration, description }, html),
      app: integration.app ?? null,
      enable_link: integration.enable_link ?? null,
      image: integration.image ?? null,
      weight: integration.weight ?? null,
      popular: integration.popular ?? null,
      resources: integration.resources ?? null,
      features: integration.features ?? null,
      connectors: integration.connectors
        ? {
            heading: integration.connectors.heading ?? null,
            cards: integration.connectors.cards.map((connector) => ({
              heading: connector.heading,
              copy: connector.copy ?? null,
              url: connector.url,
            })),
          }
        : null,
      cta: integration.cta
        ? {
            heading: integration.cta.heading ?? null,
            button: integration.cta.button ?? null,
            image: integration.cta.image ?? null,
          }
        : null,
      _category: integration._category ?? null,
      icon: integration.icon ?? null,
    };
  });

export const loadAcademyLesson = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data: { slug } }): Promise<AcademyLessonView> => {
    const lesson = getAcademyLesson(slug);
    if (!lesson) throw notFound();
    const body = await getAcademyBody(slug, lesson._filename);
    const html = await renderMarkdown(body);
    return {
      ...baseView(lesson, html),
      speaker: lesson.speaker ?? null,
      speaker_title: lesson.speaker_title ?? null,
      video_url: lesson.video_url ?? null,
      length: lesson.length ?? null,
      description_short: lesson.description_short ?? null,
      summary_heading: lesson.summary_heading ?? null,
      summary_list: lesson.summary_list ?? null,
      toc: extractToc(html),
      learn_more: lesson.learn_more ?? null,
    };
  });

type AcademyNavigationCategory = {
  heading: string;
  posts: string[];
};

export const loadAcademyNavigation = createServerFn({ method: "GET" })
  .handler(async () => {
    const fm = getStandaloneFrontmatter("c1-academy/_index.md");
    const raw = fm?.navigation;

    if (!Array.isArray(raw)) return [] as AcademyNavigationCategory[];

    return raw
      .map((c) => {
        const heading = (c as { heading?: unknown }).heading;
        const posts = (c as { posts?: unknown }).posts;
        return {
          heading: typeof heading === "string" ? heading : "",
          posts: Array.isArray(posts) ? (posts.filter((p) => typeof p === "string") as string[]) : [],
        } satisfies AcademyNavigationCategory;
      })
      .filter((c) => c.heading && c.posts.length > 0);
  });

export const loadLegalPage = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => d)
  .handler(async ({ data: { slug } }): Promise<LegalPageView> => {
    const page = getLegalPage(slug);
    if (!page) throw notFound();
    const body = await getLegalBody(slug, page._filename);
    const html = await renderMarkdown(body);
    return baseView(page, html);
  });

// ================================================================
// List-page loaders (frontmatter only, paginated)
// ================================================================

export const loadBlogList = createServerFn({ method: "GET" })
  .inputValidator((d: { page: number }) => d)
  .handler(async ({ data: { page } }) => {
    const allPosts = filterForList(getAllBlogPosts());
    const result = paginate(allPosts, page);

    // Featured post (page 1 only)
    let featuredPost: {
      slug: string;
      title: string;
      description: string;
      date: string;
      featured_image: string;
      authors: ReturnType<typeof getAuthors>;
    } | null = null;
    if (page === 1) {
      const indexFm = getStandaloneFrontmatter("blog/_index.md");
      const featuredSlug = (indexFm?.featured_post as string | undefined)
        ?.replace(/^blog\//, "")
        .replace(/\/$/, "");
      if (featuredSlug) {
        const fp = getBlogPost(featuredSlug);
        if (fp) {
          featuredPost = {
            slug: fp.slug,
            title: fp.title,
            description: fp.description,
            date: fp.date,
            featured_image: fp.featured_image,
            authors: getAuthors((fp as BlogPost).author),
          };
        }
      }
    }

    // All unique categories for filter dropdown.
    // The UI filters using a normalized value (lowercase + spaces -> dashes),
    // so de-dupe on that normalized value to avoid duplicates like
    // "Security" vs "security" or "User Access" vs "User  Access".
    const categoryLabelByValue = new Map<string, string>();
    for (const p of allPosts) {
      const cats = (p as BlogPost).categories || [];
      for (const c of cats) {
        const value = c.toLowerCase().replace(/\s+/g, "-");
        if (!categoryLabelByValue.has(value)) categoryLabelByValue.set(value, c);
      }
    }
    const allCategories = [...categoryLabelByValue.values()].sort((a, b) => a.localeCompare(b));

    return {
      posts: result.items.map((p) => ({
        slug: p.slug,
        title: p.title,
        description: p.description,
        date: p.date,
        featured_image: p.featured_image,
        authors: getAuthors((p as BlogPost).author),
        categories: (p as BlogPost).categories,
      })),
      page: result.page,
      totalPages: result.totalPages,
      featuredPost,
      allCategories,
    };
  });

/**
 * Lightweight search index over ALL blog posts (not paginated).
 *
 * Used by the client-side search box on /blog/ to filter across every
 * post, not just the current page. Kept intentionally small: no body,
 * no MDX, just the fields needed to render a BlogCard plus the topic
 * list used for scoring.
 */
export const loadBlogSearchIndex = createServerFn({ method: "GET" })
  .handler(async () => {
    const allPosts = filterForList(getAllBlogPosts());
    return {
      posts: allPosts.map((p) => ({
        slug: p.slug,
        title: p.title,
        description: p.description,
        date: p.date,
        featured_image: p.featured_image,
        authors: getAuthors((p as BlogPost).author),
        // `categories` doubles as "topics" in our frontmatter. Used for
        // matching search queries like "AI Access Management" against
        // posts tagged with that topic. Not rendered in the UI.
        topics: (p as BlogPost).categories || [],
      })),
    };
  });

export const loadEngineeringList = createServerFn({ method: "GET" })
  .inputValidator((d: { page: number }) => d)
  .handler(async ({ data: { page } }) => {
    const allPosts = filterForList(getAllEngineeringPosts());
    const result = paginate(allPosts, page);
    return {
      posts: result.items.map((p) => ({
        slug: p.slug,
        title: p.title,
        description: p.description,
        date: p.date,
        featured_image: p.featured_image,
        authors: getAuthors((p as EngineeringPost).author),
        categories: (p as EngineeringPost).categories,
      })),
      page: result.page,
      totalPages: result.totalPages,
    };
  });

export const loadAllEngineeringPosts = createServerFn({ method: "GET" })
  .handler(async () => {
    return filterForList(getAllEngineeringPosts()).map((p) => ({
      slug: p.slug,
      title: p.title,
      description: p.description,
      date: p.date,
      featured_image: p.featured_image,
      authors: getAuthors((p as EngineeringPost).author),
      categories: (p as EngineeringPost).categories,
    }));
  });

export const loadGuideList = createServerFn({ method: "GET" })
  .handler(async () => {
    return filterForList(getAllGuides()).map((g) => ({
      slug: g.slug,
      title: g.title,
      description: g.description,
      date: g.date,
      featured_image: g.featured_image,
    }));
  });

export const loadGlossaryList = createServerFn({ method: "GET" })
  .handler(async () => {
    const entries = filterForList(getAllGlossaryEntries());
    const groups: Record<string, { slug: string; title: string }[]> = {};
    for (const entry of entries) {
      const letter = ((entry as GlossaryEntry).term || entry.title[0] || "?").toUpperCase();
      if (!groups[letter]) groups[letter] = [];
      groups[letter].push({ slug: entry.slug, title: entry.title });
    }

    // Load _index.md for featured_post reference
    const indexFm = getStandaloneFrontmatter("glossary/_index.md");
    const featuredSlug = (indexFm?.featured_post as string | undefined)
      ?.replace(/^\/glossary\//, "")
      .replace(/\/$/, "");

    // Featured post
    let featured: {
      slug: string;
      title: string;
      description: string;
      featured_image: string;
    } | null = null;
    if (featuredSlug) {
      const fp = getGlossaryEntry(featuredSlug);
      if (fp) {
        featured = {
          slug: fp.slug,
          title: fp.title,
          description: fp.description,
          featured_image: fp.featured_image,
        };
      }
    }

    // Latest 3 articles by date (excluding featured). `entries` stays
    // alphabetical for `groups`; sort a copy by date descending here.
    const latest = [...entries]
      .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
      .filter((e) => e.slug !== featuredSlug)
      .slice(0, 3)
      .map((e) => ({
        slug: e.slug,
        title: e.title,
        description: e.description,
      }));

    return { groups, featured, latest };
  });

export const loadNewsList = createServerFn({ method: "GET" })
  .handler(async () => {
    return filterForList(getAllNewsArticles()).map((a) => ({
      slug: a.slug,
      title: a.title,
      description: a.description,
      date: a.date,
    }));
  });

export const loadPodcastList = createServerFn({ method: "GET" })
  .handler(async () => {
    return filterForList(getAllPodcastEpisodes()).map((e) => ({
      slug: e.slug,
      title: e.title,
      description: e.description,
      date: e.date,
      featured_image: e.featured_image,
      thumbnail: (e as PodcastEpisode).thumbnail,
    }));
  });

export const loadTalkList = createServerFn({ method: "GET" })
  .handler(async () => {
    return filterForList(getAllTalks()).map((t) => ({
      slug: t.slug,
      title: t.title,
      description: t.description,
      date: t.date,
      featured_image: t.featured_image,
      speaker: (t as Talk).speaker,
      speaker_title: (t as Talk).speaker_title,
      // Same leak class as loadTalk above: the index page never renders
      // featured_video, but it was shipping in this list's hydration
      // payload for gated talks too. Strip it here as well.
      featured_video: (t as Talk).hubspot_form_id
        ? undefined
        : (t as Talk).featured_video,
      length: (t as Talk).length,
    }));
  });

export const loadCustomerList = createServerFn({ method: "GET" })
  .handler(async () => {
    return filterForList(getAllCustomers()).map((c) => ({
      slug: c.slug,
      title: c.title,
      description: c.description,
      featured_image: c.featured_image,
      heading: (c as Customer).heading,
      logo: (c as Customer).logo,
      logo_light: (c as Customer).logo_light,
      industries: (c as Customer).industries,
    }));
  });

export const loadIntegrationList = createServerFn({ method: "GET" })
  .handler(async () => {
    const categories = getIntegrationCategories();
    const allIntegrations = filterForList(getAllIntegrations());

    const mapIntegration = (i: ContentBase) => ({
      slug: i.slug,
      title: i.title,
      app: (i as Integration).app ?? i.title,
      icon: (i as Integration).icon,
      image: (i as Integration).image,
      enable_link: (i as Integration).enable_link ?? false,
      _category: (i as Integration)._category,
    });

    // "Most popular" virtual category — integrations with popular: true
    const popular = allIntegrations
      .filter((i) => (i as Integration).popular)
      .sort((a, b) => ((a as Integration).weight ?? 0) - ((b as Integration).weight ?? 0))
      .map(mapIntegration);

    const byCategory = [
      {
        category: "most-popular",
        label: "Most popular",
        icon: undefined as string | undefined,
        integrations: popular,
      },
      ...categories.map((cat) => {
        const catIndex = getStandaloneFrontmatter(`integrations/${cat}/_index.md`);
        const catTitle = (catIndex?.title as string) || cat.split("-").map((w) => w[0].toUpperCase() + w.slice(1)).join(" ");
        const catIcon = (catIndex?.icon as string) || undefined;

        return {
          category: cat,
          label: catTitle,
          icon: catIcon,
          integrations: allIntegrations
            .filter((i) => (i as Integration)._category === cat)
            .sort((a, b) => ((a as Integration).weight ?? 0) - ((b as Integration).weight ?? 0))
            .map(mapIntegration),
        };
      }),
    ];

    return {
      byCategory,
      total: allIntegrations.length,
    };
  });

export const loadIntegrationsByCategory = createServerFn({ method: "GET" })
  .inputValidator((d: { category: string }) => d)
  .handler(async ({ data: { category } }) => {
    return getIntegrationsByCategory(category).map((i) => ({
      slug: i.slug,
      title: i.title,
      description: i.description,
      _category: i._category,
      icon: i.icon,
      image: i.image,
    }));
  });

export const loadAcademyList = createServerFn({ method: "GET" })
  .handler(async () => {
    return filterForList(getAllAcademyLessons()).map((l) => ({
      slug: l.slug,
      title: l.title,
      description: l.description,
      featured_image: l.featured_image,
      description_short: (l as AcademyLesson).description_short,
      speaker: (l as AcademyLesson).speaker,
      speaker_title: (l as AcademyLesson).speaker_title,
      length: (l as AcademyLesson).length,
    }));
  });

// ================================================================
// Block page loaders (frontmatter with blocks array)
// ================================================================

/**
 * Get the nth-latest item from a content collection (0 = latest).
 * Returns a card-shaped object for the LatestCardsDark block.
 */
function getLatestCard(source: string, offset = 0): { image: string; label: string; heading: string; copy: string; url: string } | null {
  const labelMap: Record<string, string> = {
    blog: "Blog",
    guides: "Guide",
    customers: "Story",
    engineering: "C1 Labs",
    news: "News",
    podcast: "Podcast",
    talks: "Talk",
  };
  const urlMap: Record<string, (slug: string) => string> = {
    blog: (s) => `/blog/${s}/`,
    guides: (s) => `/guides/${s}/`,
    customers: (s) => `/customers/${s}/`,
    engineering: (s) => `/labs/research/${s}/`,
    news: (s) => `/news/press-release/${s}/`,
    podcast: (s) => `/podcast/${s}/`,
    talks: (s) => `/talks/${s}/`,
  };

  let items: ContentBase[];
  switch (source) {
    case "blog": items = filterForList(getAllBlogPosts()); break;
    case "guides": items = filterForList(getAllGuides()); break;
    case "customers": items = filterForList(getAllCustomers()); break;
    case "engineering": items = filterForList(getAllEngineeringPosts()); break;
    case "news": items = filterForList(getAllNewsArticles()); break;
    case "podcast": items = filterForList(getAllPodcastEpisodes()); break;
    case "talks": items = filterForList(getAllTalks()); break;
    default: return null;
  }

  const latest = items[offset];
  if (!latest) return null;

  return {
    image: latest.featured_image || "",
    label: labelMap[source] || source,
    heading: latest.title,
    copy: latest.description || "",
    url: urlMap[source]?.(latest.slug) || `/${source}/${latest.slug}/`,
  };
}

/**
 * Hydrate latest-cards-dark blocks that have `auto: true`.
 * Preserves any explicitly declared cards before appending dynamic content.
 * `sources` defaults to ["blog", "guides", "customers"]. Repeating a source
 * walks further down that collection, so ["blog", "blog"] fills the two
 * latest posts rather than the same post twice.
 */
function hydrateLatestCards(blocks: any[]): any[] {
  return blocks.map((block) => {
    if (
      (block.block === "latest-cards-dark" ||
       block.block === "latest-cards-light" ||
       block.block === "latest-card-grid") &&
      block.auto === true
    ) {
      const sources: string[] = block.sources || ["blog", "guides", "customers"];
      const staticCards = Array.isArray(block.cards) ? block.cards : [];
      const taken: Record<string, number> = {};
      const autoCards = sources
        .map((source) => {
          const offset = taken[source] ?? 0;
          taken[source] = offset + 1;
          return getLatestCard(source, offset);
        })
        .filter((c): c is NonNullable<typeof c> => c !== null);
      const cards = [...staticCards, ...autoCards];
      return { ...block, cards };
    }
    return block;
  });
}

export const loadBlockPage = createServerFn({ method: "GET" })
  .inputValidator((d: { collection: string; slug: string }) => d)
  .handler(async ({ data: { collection, slug } }) => {
    let page: BlockPage | undefined;
    switch (collection) {
      case "lp": page = getLandingPage(slug); break;
      case "solutions": page = getSolution(slug); break;
      case "solutions-teams": page = getSolutionsTeam(slug); break;
      case "products": page = getProduct(slug); break;
      case "sp": page = getSpPage(slug); break;
      case "register": page = getRegisterPage(slug); break;
      case "resources": page = getResourcePage(slug); break;
      case "industry": page = getIndustryPage(slug); break;
      case "partners": page = getPartnerPage(slug); break;
      default: throw notFound();
    }
    if (!page) throw notFound();
    // Hydrate any auto latest-cards blocks with real content
    if (page.blocks) {
      page = { ...page, blocks: hydrateLatestCards(page.blocks) };
    }
    // Render markdown fields in blocks to HTML
    if (page.blocks) {
      page = { ...page, blocks: await renderBlockMarkdownFields(page.blocks) };
    }
    // BlockPage frontmatter is JSON-safe from the Vite plugin.
    return page as unknown as StandalonePageView;
  });

// ================================================================
// Standalone page loaders
// ================================================================

export const loadStandalonePage = createServerFn({ method: "GET" })
  .inputValidator((d: { path: string }) => d)
  .handler(async ({ data: { path } }): Promise<StandalonePageView> => {
    const fm = getStandaloneFrontmatter(path);
    if (!fm) throw notFound();
    // Hydrate latest-cards blocks with `auto: true` so standalone pages
    // (like /products/identity-security-platform) can use the same
    // auto-source pattern as collection pages.
    let page = fm as any;
    if (page.blocks) {
      page = {
        ...page,
        blocks: await renderBlockMarkdownFields(hydrateLatestCards(page.blocks), "headings"),
      };
    }
    return page as StandalonePageView;
  });

export const loadStandalonePageWithBody = createServerFn({ method: "GET" })
  .inputValidator((d: { path: string }) => d)
  .handler(async ({ data: { path } }): Promise<StandalonePageView> => {
    const fm = getStandaloneFrontmatter(path);
    if (!fm) throw notFound();
    const body = await getStandaloneBody(path);
    const html = body.trim() ? await renderMarkdown(body) : "";
    let page = fm as StandalonePageView;
    if (page.blocks) {
      page = { ...page, blocks: await renderBlockMarkdownFields(page.blocks, "headings") };
    }
    return { ...page, html };
  });

export const loadSecurityPage = createServerFn({ method: "GET" })
  .handler(async () => {
    const indexFm = getStandaloneFrontmatter("security/_index.md");
    const blocksFm = getStandaloneFrontmatterAll("security/blocks/");
    const blocksBodies = await getStandaloneBodyAll("security/blocks/");

    const renderedBlocks: Record<string, string> = {};
    for (const [key, body] of Object.entries(blocksBodies)) {
      renderedBlocks[key] = body.trim() ? await renderMarkdown(body) : "";
    }

    return {
      index: (indexFm ?? null) as StandalonePageView | null,
      blocks: blocksFm as Record<string, StandalonePageView>,
      renderedBlocks,
    };
  });

// ================================================================
// Homepage: load slider customers (for testimonials section)
// ================================================================

/**
 * Load customer pages that have `slider` param set,
 * sorted by slider value ascending.
 * Matches Hugo: where (where site.RegularPages "Section" "customers") "Params.slider" "!=" nil
 */
export const loadSliderCustomers = createServerFn({ method: "GET" })
  .handler(() => {
    const all = getAllCustomers();
    return all
      .filter((c) => c.slider != null)
      .sort((a, b) => (a.slider ?? 0) - (b.slider ?? 0))
      .map((c) => ({
        slug: c.slug,
        featured_image: c.featured_image ?? null,
        logo_light: c.logo_light ?? null,
        video_url: c.video_url ?? null,
        quote_copy: c.quote?.copy ?? null,
        quote_author_name: c.quote?.author?.name ?? null,
        quote_author_title: c.quote?.author?.title ?? null,
        url: `/customers/${c.slug}/`,
      }));
  });

export const loadLatestCards = createServerFn({ method: "GET" })
  .handler(() => {
    const sources = ["blog", "guides", "customers"];
    return sources
      .map(getLatestCard)
      .filter((c): c is NonNullable<ReturnType<typeof getLatestCard>> => c !== null);
  });
